import { useIntl } from 'react-intl'
import { connect } from 'react-redux'
import { LuxRichInputText, LuxAutocomplete } from '@lux/wardrobe-lib'
import SectionItem from '@components/Layout/SectionItem/SectionItem'
import ProjectTitle from '@/app/components/ActivityCreation/MainInfo/ProjectTitle/ProjectTitle'
import AddAttachment from '@components/UI/AddAttachment/AddAttachment'
import SectionItemContainer from '@components/Layout/SectionItemContainer/SectionItemContainer'
import { FormBase, VisitMainInfoForm } from '@models/new-activity-form'
import { NewActivitySectionType } from '@/app/models/new-activity-section-type'
import { VisitSurvey } from '@/app/models/new-activity-creation-state'
import { FILTERS_REDUCER_NAMESPACE } from '@/app/redux/reducers/filters'
import { ProjectsService } from '@/app/services/ProjectsService'
import { useEffect, useState } from 'react'
import { FiltersService } from '@/app/services/FiltersService'
import { layoutActions } from '@Techedge/digitalportal-shared'
import { visitForm } from '@/app/models/new-activity-visit-form'
import Attachment from '@/app/components/UI/Attachment/Attachment'
import { StoreProps } from '@/app/models/store'
import { useVisitExport } from '@/app/hooks/useVisitExport'
import './VisitMainInfo.scss'
import Grsd from '@/app/containers/Grsd'
interface VisitMainInfoProps extends FormBase<VisitMainInfoForm> {
    selectedSurveys: VisitSurvey[]
    jwt: string
    setGrsdToken: (grsdToken: string, grsdParams: string) => void
    setLoading: (loading: boolean) => void
    setHideFooter: () => void
}
const VisitMainInfo = (props: VisitMainInfoProps) => {
    const componentClass = 'smart-new-activity-visit-main-info'
    const intl = useIntl()
    const { onDownload } = useVisitExport()

    const {
        form,
        selectedSurveys,
        isDone,
        jwt,
        onSetForm,
        onSave,
        setLoading,
        setGrsdToken,
        setHideFooter,
    } = props

    const [autocompleteStoreValue, setAutocompleteStoreValue] = useState(
        form.storeId?.value ? `${form.storeId.value} - ${form.storeName}` : ''
    )
    const [autocompleteStorePredictions, setAutocompleteStorePredictions] = useState([])
    const [isAutocompleteStoreLoading, setIsAutocompleteStoreLoading] = useState(false)
    const [showGrsdModal, setShowGrsdModal] = useState(false)
    const [token, setToken] = useState('')
    const [lastVisits, setLastVisits] = useState<
        { id: string; projectTitle: string; startDate: number }[]
    >([])

    const onUpload = (files) => {
        onSetForm({
            ...form,
            attachmentList: {
                value: [
                    ...form.attachmentList.value,
                    ...files.map((file: any) => ({
                        fileBlob: file.file,
                        file: file.file,
                        url: file.url,
                        id: file.id,
                    })),
                ],
            },
        })
    }

    const onRemoveAttachment = (file) => {
        let newList = [...form.attachmentList.value]
        const fileIndex = newList.findIndex((i) => i.file.name === file.name)
        newList.splice(fileIndex, 1)
        onSetForm({
            ...form,
            attachmentList: {
                value: [...newList],
            },
        })
    }

    const onSearchAutocompleteStore = (search: string) => {
        setIsAutocompleteStoreLoading(true)
        FiltersService.GetStoreList({
            mode: 'standard',
            coneOfVision: true,
            tableFilters: { search },
        }).then((res) => {
            setAutocompleteStorePredictions(res.data.stores)
            setIsAutocompleteStoreLoading(false)
        })
    }

    const autocompleteStoreItemTemplate = (item: StoreProps) => {
        return <>{`${item.store_id} - ${item.store_name}`}</>
    }

    const getGrsdToken = () => {
        ProjectsService.GetGRSDToken()
            .then((res: any) => {
                if (res.data?.access_token) {
                    setToken(res.data?.access_token)
                }
            })
            .finally(() => setLoading(false))
    }

    const onDownloadHandler = (visit: { id: string; projectTitle: string; startDate: number }) => {
        onDownload({
            projectId: visit.id,
            projectName: visit.projectTitle,
            jwt,
            surveySvtIds: selectedSurveys.map((s) => s.surveyId),
            setLoading,
        })
    }

    const openGrsd = () => {
        setGrsdToken(
            token,
            `&store_id=${form.storeId.value}`
        )
        setShowGrsdModal(true)
    }

    const onCloseGrsd = () => {
        setShowGrsdModal(false)
        setHideFooter()
    }
    useEffect(() => {
        if (form.storeId.value || (form.storeId.value && selectedSurveys.length > 0)) {
            const body: { storeIds: string[]; surveyIds?: string[] } = {
                storeIds: [form.storeId.value],
            }
            if (selectedSurveys.length > 0) {
                body.surveyIds = selectedSurveys.map((s) => s.surveyId)
            }
            ProjectsService.GetLastVisits(body).then((res) => {
                setLastVisits(res.data.projects)
            })
        }
    }, [form.storeId.value, selectedSurveys])

    useEffect(() => {
        getGrsdToken()
    }, [])

    return (
        <SectionItemContainer
            hideFooter={true}
            onSave={() => onSave()}
            onClear={() => onSetForm({ ...visitForm[NewActivitySectionType.MAIN_INFO] })}
            isDone={isDone}>
            <SectionItem>
                <ProjectTitle form={form} onSetForm={(form) => onSetForm(form)}>
                    <></>
                </ProjectTitle>
            </SectionItem>
            <SectionItem>
                <LuxAutocomplete
                    label={intl.formatMessage({
                        id: 'modals.task.photo.details.store.storeId',
                        defaultMessage: 'Store ID',
                    })}
                    value={autocompleteStoreValue}
                    predictions={autocompleteStorePredictions}
                    minDigit={2}
                    additionalProps={{
                        appendTo: 'self',
                    }}
                    mandatory
                    isLoading={isAutocompleteStoreLoading}
                    onChangeCallback={setAutocompleteStoreValue}
                    onClear={() => {
                        setAutocompleteStoreValue('')
                    }}
                    onSearchPredictions={onSearchAutocompleteStore}
                    onPredictionSelected={(value) => {
                        onSetForm({
                            ...form,
                            storeId: { value: value.store_id },
                            storeName: value.store_name,
                        })
                        setAutocompleteStoreValue(`${value.store_id} - ${value.store_name}`)
                    }}
                    placeholder={intl.formatMessage({
                        id: 'modals.task.photo.details.store_field.placeholder',
                        defaultMessage: 'Search by Store ID, Store name or address',
                    })}
                    itemTemplate={autocompleteStoreItemTemplate}
                />
            </SectionItem>
            <div className={`${componentClass}__field-container`}>
                <LuxRichInputText
                    value={form.info.value}
                    onChangeCallback={(value: string) => onSetForm({ ...form, info: { value } })}
                    placeholder={intl.formatMessage({
                        id: 'modals.new.activity.section.main.info.rich.text.input.placeholder',
                        defaultMessage: 'Type here',
                    })}
                    label={intl.formatMessage({
                        id: 'modals.new.activity.section.main.info.rich.text.input.label',
                        defaultMessage: 'Information',
                    })}
                    config={{
                        events: {
                            initialized() {
                                const editor = this
                                if (form.info.disabled) editor.edit.off()
                            },
                        },
                    }}
                    fileUploadUrl={ProjectsService.FROALA_UPLOAD_URL}
                />
            </div>
            <div className={`${componentClass}__field-container`}>
                <AddAttachment
                    id={'visit-main-info'}
                    form={form}
                    onUpload={(form) => onUpload(form)}
                    showRemoveIcon
                    onRemove={(file) => onRemoveAttachment(file)}
                    showSplitFile
                    disabled={form.attachmentList.disabled}
                />
            </div>
            <div className={`${componentClass}__field-container`}>
                <div className="smart-wardrobe-form-label">
                    <span className="label">
                        {intl.formatMessage({
                            id: 'modals.new.activity.section.main.info.previous.visit',
                            defaultMessage: 'Previous Visits',
                        })}
                    </span>
                </div>
                {!lastVisits.length && (
                    <span className={`${componentClass}__previous-visits text`}>
                        {intl.formatMessage({
                            id: 'modals.new.activity.section.main.info.previous.visit.empty',
                            defaultMessage: 'No previous Visits for the selected store',
                        })}
                    </span>
                )}
                {lastVisits.map((visit, index) => (
                    <div className={`${componentClass}__previous-visit-container`} key={index}>
                        <Attachment
                            showDownloadIcon={true}
                            fileUrl={''}
                            fileName={`${visit.projectTitle} - ${new Date(
                                visit.startDate
                            ).toLocaleDateString()}`}
                            fileExtension={''}
                            onDownloadCallback={onDownloadHandler.bind(null, visit)}
                            hideEyeIcon
                        />
                    </div>
                ))}
            </div>
            {form.storeId.value && (
                <div className={`${componentClass}__field-container`}>
                    <span className={`${componentClass}__previous-visits link`} onClick={openGrsd}>
                        {intl.formatMessage({
                            id: 'modals.new.activity.section.main.info.go.to.grsd',
                            defaultMessage: 'Go to GRSD',
                        })}
                    </span>
                </div>
            )}
            {showGrsdModal && <Grsd onCloseGrsd={onCloseGrsd} />}
        </SectionItemContainer>
    )
}

const mapStateToProps = (state: any) => ({
    departmentList: state[FILTERS_REDUCER_NAMESPACE].departmentList,
    jwt: state.userReducer.accessToken,
})

const mapDispatchToProps = (dispatch: any) => ({
    setLoading: (loading: boolean) => dispatch(layoutActions.setModals({ props: { loading } })),
    setGrsdToken: (grsdToken: string, grsdParams: string) =>
        dispatch(layoutActions.setModals({ props: { grsdToken, grsdParams, hideFooter: true } })),
    setHideFooter: () => dispatch(layoutActions.setModals({ props: { hideFooter: false } })),
})

export default connect(mapStateToProps, mapDispatchToProps)(VisitMainInfo)
